/**
 * Gallery — recent work as a swipe carousel.
 *
 * Client revision (Sep 2026): the bilingual eyebrow pair became a plain
 * per-locale label, the subline and the standalone Instagram button are
 * gone (socials consolidated in the footer), and the grid became the
 * shared scroll-snap Carousel. Order and filenames still come from
 * shop.config.json; each label doubles as the image's alt text so /es
 * gets Spanish alt rather than English.
 */

import { useCopy } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { BrassRule } from './BrassRule';
import Carousel from './Carousel';

export default function Gallery() {
  const c = useCopy().home.gallery;
  const { t, config } = useShopConfig();

  return (
    <section id="gallery" className="bg-white px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-col gap-3 md:mb-14">
          <p className="font-oswald text-[12px] uppercase tracking-[0.24em] text-warm-muted">
            {t(c.eyebrowSimple)}
          </p>
          <BrassRule width={48} className="mt-3 mb-6" />
          <h2 className="text-balance font-newsreader text-[34px] font-medium leading-[1.1] text-navy md:text-[46px]">
            {t(c.headline)}
          </h2>
        </div>

        <Carousel label={t(c.headline)}>
          {config.gallery.map((item) => {
            const label = t(c.labels[item.slug]);
            return (
              <figure
                key={item.slug}
                className="group relative aspect-[3/4] w-[85%] shrink-0 snap-start overflow-hidden rounded-card bg-edge-1 shadow-[0_16px_40px_rgba(11,27,58,0.08)] sm:w-[45%] lg:w-[30%]"
              >
                <img
                  src={item.src}
                  alt={label}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/90 via-navy/60 to-transparent px-5 pb-4 pt-16 font-newsreader text-[18px] font-medium text-white">
                  {label}
                </figcaption>
              </figure>
            );
          })}
        </Carousel>
      </div>
    </section>
  );
}
